import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Tag, Users, User, MapPin, Percent, Building2 } from "lucide-react";
import { getAdminUser, clearAdminAuth } from "../../helper/adminAuthStorage";
import "./adminPricing.css";

const LOGO = `${process.env.wordpressApiEndPoint || ""}/wp-content/uploads/2025/07/logo-new.svg`;

const NAV_PRICING = [
  { to: "global-pricing", label: "Global Pricing", icon: <Tag size={18} strokeWidth={1.8} /> },
  { to: "group-pricing", label: "Group Pricing", icon: <Users size={18} strokeWidth={1.8} /> },
  { to: "user-pricing", label: "User Pricing", icon: <User size={18} strokeWidth={1.8} /> },
  { to: "city-manage", label: "City Manage", icon: <MapPin size={18} strokeWidth={1.8} /> },
  { to: "vat-manage", label: "Manage Tour VAT", icon: <Percent size={18} strokeWidth={1.8} /> },
];

const NAV_INVENTORY = [
  { to: "hotels", label: "Hotels", icon: <Building2 size={18} strokeWidth={1.8} /> },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getAdminUser();
  const [logoOk, setLogoOk] = React.useState(true);

  const seg = location.pathname.replace(/^\/admin\/?/, "").split("/")[0];
  const isHotels = seg === "hotels";
  const navItems = isHotels ? NAV_INVENTORY : NAV_PRICING;
  const navTitle = isHotels ? "Inventory" : "Pricing";
  const headerTag = isHotels ? "Manage Hotel Inventory" : "Manage Excursion Pricing";
  const switchTo = (toHotels) => navigate(toHotels ? "/admin/hotels" : "/admin/global-pricing");

  const logout = () => {
    clearAdminAuth();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="adm-root">
      <style>{`
        .adm-root{min-height:100vh;background:#f2f4f9;color:#1b2439;}
        .adm-top{height:60px;background:#fff;border-bottom:1px solid #e6eaf2;display:flex;align-items:center;
          justify-content:space-between;padding:0 22px;position:sticky;top:0;z-index:20;gap:16px;}
        .adm-brand{display:flex;align-items:center;gap:10px;font-weight:800;color:#283D6B;font-size:15px;}
        .adm-brand img{height:45px;width:auto;}
        .adm-brand .tag{font-weight:600;color:#7a8699;font-size:14px;padding-left:10px;}
        .adm-switch{display:flex;border:1px solid #e2e6ee;border-radius:9px;overflow:hidden;flex-shrink:0;}
        .adm-switch button{border:none;background:#fff;padding:8px 18px;font-size:13px;font-weight:700;color:#5b6577;cursor:pointer;font-family:inherit;}
        .adm-switch button + button{border-left:1px solid #e2e6ee;}
        .adm-switch button:hover:not(.on){background:#f4f7fb;}
        .adm-switch button.on{background:#3264FF;color:#fff;}
        .adm-user{display:flex;align-items:center;gap:12px;font-weight:600;}
        .adm-user .nm{font-size:14px;color:#5b6577;}
        .adm-logout{border:1px solid #e2e6ee;background:#fff;color:#5b6577;border-radius:8px;padding:7px 13px;
          font-size:13px;font-weight:600;cursor:pointer;}
        .adm-logout:hover{border-color:#3264FF;color:#3264FF;}
        .adm-body{display:flex;flex:1;min-height:0;align-items:flex-start;}
        .adm-side{width:230px;flex-shrink:0;background:#fff;border-right:1px solid #e6eaf2;padding:16px 12px;
          position:sticky;top:60px;height:calc(100vh - 60px);overflow-y:auto;}
        .adm-navttl{font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#9aa4b8;margin:6px 10px 12px;}
        .adm-link{display:flex;align-items:center;gap:11px;padding:11px 12px;border-radius:10px;color:#5b6577;
          text-decoration:none;font-size:14px;font-weight:600;margin-bottom:4px;}
        .adm-link:hover{background:#f4f7fb;color:#283D6B;}
        .adm-link.active{background:linear-gradient(90deg,#eef2ff,#f4f7ff);color:#2b4fd6;}
        .adm-link.active svg{color:#3264FF;}
        .adm-link svg{color:#98a2b6;}
        .adm-main{flex:1;min-width:0;padding:22px;}
        @media (max-width:820px){
          .adm-top{flex-wrap:wrap;height:auto;padding:10px 14px;}
          .adm-body{flex-direction:column;}
          .adm-side{position:static;height:auto;width:100%;display:flex;gap:6px;overflow-x:auto;padding:10px;}
          .adm-navttl{display:none;}
          .adm-link{margin-bottom:0;white-space:nowrap;}
        }
      `}</style>

      <div className="adm-top">
        <div className="adm-brand">
          {logoOk ? (
            <img src={LOGO} alt="Dahrtours" onError={() => setLogoOk(false)} />
          ) : (
            <span>Dahr<span style={{ color: "#3264FF" }}>tours</span></span>
          )}
          <span className="tag">{headerTag}</span>
        </div>
        {/* <div className="adm-switch">
          <button type="button" className={!isHotels ? "on" : ""} onClick={() => switchTo(false)}>Excursions</button>
          <button type="button" className={isHotels ? "on" : ""} onClick={() => switchTo(true)}>Hotels</button>
        </div> */}
        <div className="adm-user">
          <span className="nm">{user?.display_name || user?.username || "Admin"}</span>
          <button className="adm-logout" onClick={logout}>Logout</button>
        </div>
      </div>

      <div className="adm-body">
        <aside className="adm-side">
          <div className="adm-navttl">{navTitle}</div>
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `adm-link${isActive ? " active" : ""}`}
            >
              {n.icon}
              {n.label}
            </NavLink>
          ))}
        </aside>
        <main className="adm-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
